import axios from 'axios';
import i18n from 'i18next';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import type {
  IAnnouncementClientFolder,
  IAttachment,
  IAttachmentError,
  IBaseClientFolder,
  IClient,
  IDatabaseClientFolder,
  IDocumentClientFolder,
  IGetAttachmentsRequest,
  IGetDatabaseViewRequest,
  IGetDatabaseViewsListRequest,
  IGetTreeRequest,
  ILinkClientFolder,
  IRawResourceNodes,
  IResourceCreateAnnouncement,
  IResourceCreateDocument,
  IResourceCreateFolder,
  IResourceCreateLink,
  IResourceDatabaseField,
  IResourceDatabaseView,
  IResourceDeleteAnnouncement,
  IResourceDeleteDocument,
  IResourceDeleteFolder,
  IResourceDeleteLink,
  IResourceEditFolder,
  IResourceFavoriteRequest,
  IResourceDatabaseFieldType,
  IResourceItemType,
  IResourceUpdateAnnouncement,
  IResourceUpdateDocument,
  IResourceUpdateLink,
  IUpdateClientTreeBranchRequest,
  IResourceGetMemberListRequest,
  IResourceClientMember,
  ILatestDatabaseItem,
  IGetLatestDatabaseItemsRequest,
  IResourceDatabaseRecord,
} from '@/api/types';
import type {
  IResourcesState,
  IThunkAPI,
  IUpdatedDatabaseFolderTreeBranch,
  IUpdatedFolderTreeBranch,
} from '@/store/types';
import type { IResourceType, ITreeFolder } from '@/components/common';
import type { StandardRequest, RootState, PayloadRequest } from '@/store';
import type { IResourceDeleteDatabaseItemDispatch, IResourceUpsertDatabaseItemDispatch } from '../types/resourceSlice';

import {
  getResourceTypeId,
  isActionFromSlice,
  isAttachementError,
  isFulfilledAction,
  isKnownError,
  isOfType,
  isPendingAction,
  isRejectedAction,
  logError,
  makeCancellable,
  onFulfilledAction,
  onPendingAction,
  onRejectedAction,
} from '@/helpers';
import { resources as api } from '@/api';
import { ResourceTypeId, UserType } from '@/store/enums';
import {
  transformRawResourceItem,
  transformResourceTreeAnnouncementNodes,
  transformResourceTreeDatabaseNodes,
  transformResourceTreeDocumentNodes,
  transformResourceTreeLinkNodes,
} from '@/api/transforms/resources';
import { SLICES } from '@/constants/actions';
import { RECORDS_TABLE_PAGE_SIZE, SYSTEM_CLIENT_ID, SYSTEM_CLIENT_NAME } from '@/config/variables';

const initialState: IResourcesState = {
  foldersList: null,
  favorites: null,
  selectedResourceDetails: null,
  selectedAttachments: [],
  isPreviewOpen: false,
  databaseFieldTypeDict: [],
  latestDatabaseItems: [],
  getResourceSummaryList: [],
  databaseViewQueryParams: {
    pageNum: 1,
    pageSize: RECORDS_TABLE_PAGE_SIZE,
    sortDirection: 'ASC',
    text: '',
    sortBy: '',
  },
  meta: {
    pending: [],
  },
};

// get resource summary list
export const getResourceSummaryList = createAsyncThunk<any, void, IThunkAPI>(
  'resourceSummary/list',
  async (_, thunkAPI) => {
    try {
      const response = await api.getResourcesSummaryList();

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getTree = createAsyncThunk<ITreeFolder[], undefined, IThunkAPI>(
  'resources/getTree',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const agentContractClients = state.auth.user?.agentContractClients;
      const userContractClients = state.auth.user?.availableClients;
      const currentClientId = state.auth.user?.currentClient?.clientId;
      const userTypeId = state.auth.menuType ?? UserType.unauthorized;

      if (!userTypeId) {
        throw new Error('User not authenticated.');
      }

      const getLinkTree = makeCancellable(api.getLinkTree, thunkAPI.signal);
      const getAnnouncementTree = makeCancellable(api.getAnnouncementTree, thunkAPI.signal);
      const getDocumentTree = makeCancellable(api.getDocumentTree, thunkAPI.signal);
      const getDatabaseTree = makeCancellable(api.getDatabaseTree, thunkAPI.signal);

      const requestPayload: IGetTreeRequest = {
        userType: userTypeId,
        clientId: userTypeId !== UserType.agent && currentClientId ? currentClientId : undefined,
      };

      const [linksResponse, documentsResponse, announcementResponse, databaseResponse] = await Promise.allSettled([
        getLinkTree(requestPayload),
        getDocumentTree(requestPayload),
        getAnnouncementTree(requestPayload),
        getDatabaseTree(requestPayload),
      ]);

      const items = {
        links: [] as ILinkClientFolder[],
        documents: [] as IDocumentClientFolder[],
        announcements: [] as IAnnouncementClientFolder[],
        databases: [] as IDatabaseClientFolder[],
      };

      if (linksResponse.status === 'rejected' || isKnownError(linksResponse.value.data)) {
        toast.error(i18n.t('resources.errors.fetchingLinksFailed'));
        console.error(linksResponse);
      } else {
        items.links = transformResourceTreeLinkNodes(linksResponse.value.data);
      }

      if (documentsResponse.status === 'rejected' || isKnownError(documentsResponse.value.data)) {
        toast.error(i18n.t('resources.errors.fetchingDocumentsFailed'));
        console.error(documentsResponse);
      } else {
        items.documents = transformResourceTreeDocumentNodes(documentsResponse.value.data);
      }

      if (announcementResponse.status === 'rejected' || isKnownError(announcementResponse.value.data)) {
        toast.error(i18n.t('resources.errors.fetchingAnnouncementsFailed'));
        console.error(announcementResponse);
      } else {
        items.announcements = transformResourceTreeAnnouncementNodes(announcementResponse.value.data);
      }

      if (databaseResponse.status === 'rejected' || isKnownError(databaseResponse.value.data)) {
        toast.error(i18n.t('resources.errors.fetchingDatabasesFailed'));
        console.error(databaseResponse);
      } else {
        items.databases = transformResourceTreeDatabaseNodes(databaseResponse.value.data);
      }

      const createTreeFolder = (client: IClient): ITreeFolder => ({
        ...client,
        clientMemberList: [],
        announcement: items.announcements.find(item => item.clientId === client.clientId)?.folders || [],
        document: items.documents.find(item => item.clientId === client.clientId)?.folders || [],
        link: items.links.find(item => item.clientId === client.clientId)?.folders || [],
        database: items.databases.find(item => item.clientId === client.clientId)?.databases || [],
      });

      if (userTypeId === UserType.agent) {
        if (!agentContractClients) throw new Error('Missing agent contract clients.');

        const systemClient = {
          clientId: SYSTEM_CLIENT_ID,
          clientName: SYSTEM_CLIENT_NAME,
          clientShortName: SYSTEM_CLIENT_NAME,
        };

        const uniqueIds = Array.from(new Set(agentContractClients.map(item => item.clientId)));
        const uniqueClients = uniqueIds.flatMap(
          id => agentContractClients.find(client => client.clientId === id) || [],
        );

        return [...uniqueClients, systemClient].map(createTreeFolder);
      }

      if (!userContractClients) throw new Error('Missing user contract clients.');

      const fetchedClient = userContractClients.find(client => client.clientId === currentClientId);

      if (!fetchedClient) {
        return state.resources.foldersList || [];
      }

      const newClientFolder = createTreeFolder(fetchedClient);

      if (state.resources.foldersList) {
        if (state.resources.foldersList.some(clientFolder => clientFolder.clientId === currentClientId)) {
          return state.resources.foldersList.map(clientFolder =>
            clientFolder.clientId === currentClientId ? newClientFolder : clientFolder,
          );
        }

        return [...state.resources.foldersList, newClientFolder];
      }

      return [newClientFolder];
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;

      if (state.resources.meta.pending.includes('resources/getTree')) return false;

      return true;
    },
  },
);

export const updateClientBranch = createAsyncThunk<IUpdatedFolderTreeBranch, IUpdateClientTreeBranchRequest, IThunkAPI>(
  'resources/updateClientBranch',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      // TODO: Change any for a proper type
      const actions: {
        [key in IResourceType]: {
          apiCall: StandardRequest<IRawResourceNodes, IGetTreeRequest>;
          transform: (nodes: any) => IBaseClientFolder[];
        };
      } = {
        link: {
          apiCall: api.getLinkTree,
          transform: transformResourceTreeLinkNodes,
        },
        announcement: {
          apiCall: api.getAnnouncementTree,
          transform: transformResourceTreeAnnouncementNodes,
        },
        document: {
          apiCall: api.getDocumentTree,
          transform: transformResourceTreeDocumentNodes,
        },
        database: {
          apiCall: api.getDatabaseTree,
          transform: transformResourceTreeDatabaseNodes,
        },
      };

      const getTree = makeCancellable(actions[payload.resourceType].apiCall, thunkAPI.signal);
      const rawData = (await getTree({ userType: menuType, clientId: payload.clientId })).data;

      if (isKnownError(rawData)) throw rawData;

      const data = actions[payload.resourceType].transform(rawData);
      const updatedFolders = data.find(clientFolder => clientFolder.clientId === payload.clientId);

      if (!updatedFolders) throw new Error('Client not found.');

      if (payload.resourceType === 'database') {
        return {
          databases: (updatedFolders as IDatabaseClientFolder).databases,
          clientId: payload.clientId,
          type: payload.resourceType,
        };
      }

      return {
        folders: (updatedFolders as ILinkClientFolder | IDocumentClientFolder | IAnnouncementClientFolder).folders,
        clientId: payload.clientId,
        type: payload.resourceType,
      };
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getAttachments = createAsyncThunk<
  IResourcesState['selectedAttachments'],
  IGetAttachmentsRequest,
  IThunkAPI
>('resources/selectedAttachments', async (payload, thunkAPI) => {
  try {
    const source = axios.CancelToken.source();
    thunkAPI.signal.addEventListener('abort', () => {
      source.cancel('getAttachments cancelled');
    });
    const responses = await Promise.allSettled(
      payload.attachmentIds.map(attachmentId => api.getAttachment({ attachmentId }, { cancelToken: source.token })),
    );
    return responses.map((res, idx) => {
      if (res.status === 'rejected') {
        return {
          attachmentId: payload.attachmentIds[idx],
          error: res.reason,
        } as IAttachmentError;
      }

      if (typeof res.value.data === 'string') {
        return {
          attachmentId: payload.attachmentIds[idx],
          error: 'Attachment not found',
        } as IAttachmentError;
      }

      return res.value.data as IAttachment;
    });
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const setFavorite = createAsyncThunk<IResourceFavoriteRequest, IResourceFavoriteRequest, IThunkAPI>(
  'resources/setFavorite',
  async (payload, thunkAPI) => {
    try {
      const setFavorite = makeCancellable(api.setFavorite, thunkAPI.signal);
      const { data } = await setFavorite(payload);
      if (typeof data === 'string') throw data;

      return payload;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const removeFavorite = createAsyncThunk<IResourceFavoriteRequest, IResourceFavoriteRequest, IThunkAPI>(
  'resources/removeFavorite',
  async (payload, thunkAPI) => {
    try {
      const removeFavorite = makeCancellable(api.removeFavorite, thunkAPI.signal);
      const { data } = await removeFavorite(payload);
      if (typeof data === 'string') throw data;

      return payload;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getFavoritesList = createAsyncThunk<
  IResourcesState['favorites'] | number[],
  IResourceFavoriteRequest | undefined,
  IThunkAPI
>('resources/getFavoritesList', async (payload, thunkAPI) => {
  try {
    if (payload !== undefined) {
      const getFavoritesList = makeCancellable(api.getFavoritesList, thunkAPI.signal);
      const response = await getFavoritesList(payload);
      if (typeof response.data === 'string') return [];
      return response.data;
    }

    const getFavoritesListAll = makeCancellable(api.getFavoritesListAll, thunkAPI.signal);
    const { data } = await getFavoritesListAll();

    if (isKnownError(data)) throw data;

    const favorites = data.reduce<Exclude<IResourcesState['favorites'], null>>(
      (list, item) => {
        if (item.resourceTypeId === ResourceTypeId.database) {
          if (!item.databaseId) return list;
          list[item.resourceTypeId] = {
            ...list[item.resourceTypeId],
            [item.databaseId]: [...(list[item.resourceTypeId][item.databaseId] ?? []), item.itemId],
          };
          return list;
        }

        list[item.resourceTypeId].push(item.itemId);

        return list;
      },
      {
        [ResourceTypeId.announcement]: [],
        [ResourceTypeId.document]: [],
        [ResourceTypeId.link]: [],
        [ResourceTypeId.database]: {},
      },
    );

    return favorites;
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getResourceDetails = createAsyncThunk<
  IResourcesState['selectedResourceDetails'],
  IResourceItemType,
  IThunkAPI
>('resources/getResourceDetails', async (payload, thunkAPI) => {
  try {
    const { type, id } = getResourceTypeId(payload);

    const getResource = makeCancellable(api.getResource, thunkAPI.signal);
    const { data } = await getResource({ resourceType: type, resourceId: id });
    if (isKnownError(data)) throw data;

    const item = transformRawResourceItem(data);

    return item;
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const openPreview = createAsyncThunk<IResourcesState['selectedResourceDetails'], IResourceItemType, IThunkAPI>(
  'resources/openPreview',
  async (payload, thunkAPI) => {
    try {
      const { type, id } = getResourceTypeId(payload);

      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('openPreview cancelled');
      });

      const { data } = await api.getResource({ resourceType: type, resourceId: id }, { cancelToken: source.token });
      if (isKnownError(data)) throw data;

      const item = transformRawResourceItem(data);

      return item;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getDatabase = createAsyncThunk<
  {
    viewsList: IResourceDatabaseView[];
    fieldsList: IResourceDatabaseField[];
    fieldTypes: IResourceDatabaseFieldType[];
  },
  IGetDatabaseViewsListRequest,
  IThunkAPI
>(
  'resources/getDatabase',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const getDatabaseFieldsList = makeCancellable(api.getDatabaseFieldsList, thunkAPI.signal);
      const getDatabaseViewsList = makeCancellable(api.getDatabaseViewsList, thunkAPI.signal);
      const getDatabaseFieldTypes = makeCancellable(api.getDatabaseFieldTypes, thunkAPI.signal);

      const [viewsList, fieldsList, fieldTypes] = await Promise.all([
        getDatabaseViewsList({ ...payload, userType: menuType }).then(({ data }) => {
          if (isKnownError(data)) throw data;
          return data;
        }),
        getDatabaseFieldsList({ ...payload, userType: menuType }).then(({ data }) => {
          if (isKnownError(data)) throw data;
          return data;
        }),
        getDatabaseFieldTypes({ userType: menuType }).then(({ data }) => {
          if (isKnownError(data)) throw data;
          return data;
        }),
      ]);

      return { viewsList, fieldsList, fieldTypes };
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;

      return !state.resources.meta.pending.includes('resources/getDatabase');
    },
  },
);

export const getDatabaseViewsList = createAsyncThunk<IResourceDatabaseView[], IGetDatabaseViewsListRequest, IThunkAPI>(
  'resources/getDatabaseViewsList',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const getDatabaseViewsList = makeCancellable(api.getDatabaseViewsList, thunkAPI.signal);
      const { data } = await getDatabaseViewsList({ ...payload, userType: menuType });
      if (typeof data === 'string') throw data;
      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;

      return !state.resources.meta.pending.includes('resources/getDatabaseViewsList');
    },
  },
);

export const getDatabaseFieldsList = createAsyncThunk<
  IResourceDatabaseField[],
  IGetDatabaseViewsListRequest,
  IThunkAPI
>(
  'resources/getDatabaseFieldsList',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const getDatabaseFieldsList = makeCancellable(api.getDatabaseFieldsList, thunkAPI.signal);
      const { data } = await getDatabaseFieldsList({ ...payload, userType: menuType });

      if (typeof data === 'string') throw data;
      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;

      return !state.resources.meta.pending.includes('resources/getDatabaseFieldsList');
    },
  },
);

export const getDatabaseFieldTypes = createAsyncThunk<IResourceDatabaseFieldType[], undefined, IThunkAPI>(
  'resources/getDatabaseFieldTypes',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const getDatabaseFieldTypes = makeCancellable(api.getDatabaseFieldTypes, thunkAPI.signal);
      const { data } = await getDatabaseFieldTypes({ userType: menuType });

      if (typeof data === 'string') throw data;
      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;

      return !state.resources.meta.pending.includes('resources/getDatabaseFieldTypes');
    },
  },
);

export const getDatabaseView = createAsyncThunk<IResourceDatabaseView['data'], IGetDatabaseViewRequest, IThunkAPI>(
  'resources/getDatabaseView',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const getDatabaseView = makeCancellable(api.getDatabaseView, thunkAPI.signal);
      const { data } = await getDatabaseView({ ...payload, userType: menuType });
      if (typeof data === 'string') return JSON.parse(data);
      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const createLinkItem = createAsyncThunk<void, IResourceCreateLink, IThunkAPI>(
  'su/resources/createLinkItem',
  async (payload, thunkAPI) => {
    try {
      const createLink: PayloadRequest<void, IResourceCreateLink> = makeCancellable(
        api.createLink as StandardRequest<void, IResourceCreateLink>,
        thunkAPI.signal,
      );
      const { data } = await createLink(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'link', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updateLinkItem = createAsyncThunk<void, IResourceUpdateLink, IThunkAPI>(
  'su/resources/updateLinkItem',
  async (payload, thunkAPI) => {
    try {
      const updateLink: PayloadRequest<void, IResourceUpdateLink> = makeCancellable(
        api.updateLink as StandardRequest<void, IResourceUpdateLink>,
        thunkAPI.signal,
      );
      const { data } = await updateLink(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'link', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteLinkItem = createAsyncThunk<void, IResourceDeleteLink, IThunkAPI>(
  'su/resources/deleteLinkItem',
  async (payload, thunkAPI) => {
    try {
      const deleteLink = makeCancellable(api.deleteLink, thunkAPI.signal);
      const { data } = await deleteLink(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'link', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const createDocumentItem = createAsyncThunk<void, IResourceCreateDocument, IThunkAPI>(
  'su/resources/createDocumentItem',
  async (payload, thunkAPI) => {
    try {
      const createDocument = makeCancellable(api.createDocument, thunkAPI.signal);
      const { data } = await createDocument(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'document', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updateDocumentItem = createAsyncThunk<void, IResourceUpdateDocument, IThunkAPI>(
  'su/resources/updateDocumentItem',
  async (payload, thunkAPI) => {
    try {
      const updateDocument = makeCancellable(api.updateDocument, thunkAPI.signal);
      const { data } = await updateDocument(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'document', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteDocumentItem = createAsyncThunk<void, IResourceDeleteDocument, IThunkAPI>(
  'su/resources/deleteDocumentItem',
  async (payload, thunkAPI) => {
    try {
      const deleteDocument = makeCancellable(api.deleteDocument, thunkAPI.signal);
      const { data } = await deleteDocument(payload.documentId);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'document', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const createAnnouncementItem = createAsyncThunk<void, IResourceCreateAnnouncement, IThunkAPI>(
  'su/resources/createAnnouncementItem',
  async (payload, thunkAPI) => {
    try {
      const createAnnouncement = makeCancellable(api.createAnnouncement, thunkAPI.signal);
      const { data } = await createAnnouncement(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'announcement', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updateAnnouncementItem = createAsyncThunk<void, IResourceUpdateAnnouncement, IThunkAPI>(
  'su/resources/updateAnnouncementItem',
  async (payload, thunkAPI) => {
    try {
      const updateAnnouncement = makeCancellable(api.updateAnnouncement, thunkAPI.signal);
      const { data } = await updateAnnouncement(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'announcement', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteAnnouncementItem = createAsyncThunk<void, IResourceDeleteAnnouncement, IThunkAPI>(
  'su/resources/deleteAnnouncementItem',
  async (payload, thunkAPI) => {
    try {
      const deleteAnnouncement = makeCancellable(api.deleteAnnouncement, thunkAPI.signal);
      const { data } = await deleteAnnouncement(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: 'announcement', clientId: payload.client }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getLatestDatabaseItems = createAsyncThunk<
  ILatestDatabaseItem[],
  IGetLatestDatabaseItemsRequest,
  IThunkAPI
>('resources/getLatestDatabaseItems', async (payload, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as RootState;
    const userType = state.auth.user?.userTypeId;

    if (!userType) {
      throw new Error('User not authenticated.');
    }

    const getLatestDatabaseItems = makeCancellable(api.getLatestDatabaseItems, thunkAPI.signal);
    const { data } = await getLatestDatabaseItems({ ...payload, userType });
    if (isKnownError(data)) throw data;

    return data;
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const upsertDatabaseItem = createAsyncThunk<void, IResourceUpsertDatabaseItemDispatch, IThunkAPI>(
  'resources/upsertDatabaseItem',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const { viewId, ...payloadData } = payload;
      const upsertDatabaseItem = makeCancellable(api.upsertDatabaseItem, thunkAPI.signal);
      const { data } = await upsertDatabaseItem({ ...payloadData, userType: menuType });
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(
        getDatabaseView({
          clientId: payload.clientId,
          dbId: payload.databaseId,
          viewId,
          ...state.resources.databaseViewQueryParams,
        }),
      );
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteDatabaseItem = createAsyncThunk<void, IResourceDeleteDatabaseItemDispatch, IThunkAPI>(
  'resources/deleteDatabaseItem',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const menuType = state.auth.menuType ?? UserType.unauthorized;

      if (!menuType) {
        throw new Error('User not authenticated.');
      }

      const deleteDatabaseItem = makeCancellable(api.deleteDatabaseItem, thunkAPI.signal);
      const { data } = await deleteDatabaseItem({ ...payload, userType: menuType });
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(
        getDatabaseView({
          clientId: payload.clientId,
          dbId: payload.databaseId,
          viewId: payload.viewId,
          ...state.resources.databaseViewQueryParams,
        }),
      );
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getMemberList = createAsyncThunk<IResourceClientMember[], IResourceGetMemberListRequest, IThunkAPI>(
  'resources/getMemberList',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const userType = state.auth.user?.userTypeId;

      if (!userType) {
        throw new Error('User not authenticated.');
      }

      const getMemberList = makeCancellable(api.getMemberList, thunkAPI.signal);
      const { data } = await getMemberList({ ...payload, userType });
      if (isKnownError(data)) throw data;

      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const createFolder = createAsyncThunk<void, IResourceCreateFolder, IThunkAPI>(
  'resources/createFolder',
  async (payload, thunkAPI) => {
    try {
      const createFolder = makeCancellable(api.createFolder, thunkAPI.signal);
      const { data } = await createFolder(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: payload.type, clientId: payload.clientId }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updateFolder = createAsyncThunk<void, IResourceEditFolder, IThunkAPI>(
  'resources/updateFolder',
  async (payload, thunkAPI) => {
    try {
      const updateFolder = makeCancellable(api.updateFolder, thunkAPI.signal);
      const { data } = await updateFolder(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: payload.type, clientId: payload.clientId }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteFolder = createAsyncThunk<void, IResourceDeleteFolder, IThunkAPI>(
  'resources/deleteFolder',
  async (payload, thunkAPI) => {
    try {
      const deleteFolder = makeCancellable(api.deleteFolder, thunkAPI.signal);
      const { data } = await deleteFolder(payload);
      if (isKnownError(data)) throw data;

      thunkAPI.dispatch(updateClientBranch({ resourceType: payload.type, clientId: payload.clientId }));
      return;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const slice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    clearState(state) {
      state.meta = initialState.meta;
    },
    previewDatabaseViewItem(state, action: PayloadAction<IResourceDatabaseRecord>) {
      state.isPreviewOpen = true;
      state.selectedResourceDetails = action.payload;
    },
    setDatabaseViewQueryParams(state, action: PayloadAction<IResourcesState['databaseViewQueryParams']>) {
      state.databaseViewQueryParams = action.payload;
    },
    closePreview(state) {
      state.isPreviewOpen = false;
      state.selectedResourceDetails = null;
    },
    clearAttachments(state) {
      state.selectedAttachments = initialState.selectedAttachments;
    },
    clearSelectedResourceDetials(state) {
      state.selectedResourceDetails = initialState.selectedResourceDetails;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getTree.fulfilled, (state, action) => {
        state.foldersList = action.payload;
      })
      .addCase(getAttachments.fulfilled, (state, action) => {
        state.selectedAttachments = action.payload.sort(attachment => (isAttachementError(attachment) ? -1 : 1));
      })
      .addCase(openPreview.pending, state => {
        state.isPreviewOpen = true;
      })
      .addCase(openPreview.fulfilled, (state, action) => {
        state.selectedResourceDetails = action.payload;
      })
      .addCase(getResourceDetails.fulfilled, (state, action) => {
        state.selectedResourceDetails = action.payload;
      })
      .addCase(setFavorite.fulfilled, (state, action) => {
        const { resourceItemId, resourceTypeId, resourceClassId } = action.payload;
        if (resourceItemId && state.favorites) {
          if (resourceClassId) {
            (state.favorites[resourceTypeId][resourceClassId] as number[]).push(resourceItemId);
            return;
          }
          (state.favorites[resourceTypeId] as number[]).push(resourceItemId);
        }
      })
      .addCase(removeFavorite.fulfilled, (state, action) => {
        const { resourceItemId, resourceTypeId, resourceClassId } = action.payload;
        if (resourceItemId && state.favorites) {
          if (resourceClassId) {
            state.favorites[resourceTypeId][resourceClassId] = (
              state.favorites[resourceTypeId][resourceClassId] as number[]
            ).filter(id => id !== resourceItemId);
            return;
          }
          state.favorites = {
            ...state.favorites,
            [resourceTypeId]: (state.favorites[resourceTypeId] as number[]).filter(id => id !== resourceItemId),
          };
        }
      })
      .addCase(getFavoritesList.fulfilled, (state, action) => {
        const { resourceClassId, resourceTypeId } = action.meta.arg || {};
        if (resourceClassId !== undefined && resourceTypeId !== undefined) {
          if (state.favorites === null) {
            state.favorites = {
              [ResourceTypeId.announcement]: [],
              [ResourceTypeId.document]: [],
              [ResourceTypeId.link]: [],
              [ResourceTypeId.database]: {},
            };
          }
          state.favorites = {
            ...state.favorites,
            [resourceTypeId]: {
              ...state.favorites[resourceTypeId],
              [resourceClassId]: action.payload,
            },
          };
          return;
        }
        state.favorites = action.payload as IResourcesState['favorites'];
      })
      .addCase(getDatabase.fulfilled, (state, action) => {
        const { clientId, dbId } = action.meta.arg;
        const database = state.foldersList
          ?.find(client => client.clientId === clientId)
          ?.database.find(db => db.id === dbId);
        if (!database) return;

        database.folders = action.payload.viewsList.map(view => ({ ...view, folderId: view.id })) ?? [];
        database.fields = action.payload.fieldsList;
        state.databaseFieldTypeDict = action.payload.fieldTypes;
      })
      .addCase(getDatabaseViewsList.fulfilled, (state, action) => {
        const { clientId, dbId } = action.meta.arg;
        const database = state.foldersList
          ?.find(client => client.clientId === clientId)
          ?.database.find(db => db.id === dbId);
        if (!database) return;
        database.folders = action.payload.map(view => ({ ...view, folderId: view.id })) ?? [];
      })
      .addCase(getDatabaseFieldsList.fulfilled, (state, action) => {
        const { clientId, dbId } = action.meta.arg;
        const database = state.foldersList
          ?.find(client => client.clientId === clientId)
          ?.database.find(db => db.id === dbId);
        if (!database) return;
        database.fields = action.payload;
      })
      .addCase(getDatabaseFieldTypes.fulfilled, (state, action) => {
        state.databaseFieldTypeDict = action.payload;
      })
      .addCase(getDatabaseView.fulfilled, (state, action) => {
        const { clientId, dbId, viewId } = action.meta.arg;
        const view = state.foldersList
          ?.find(client => client.clientId === clientId)
          ?.database.find(db => db.id === dbId)
          ?.folders?.find(view => view.id === viewId);
        if (view && action.payload) {
          view.data = action.payload;
        }
      })
      .addCase(getMemberList.fulfilled, (state, action) => {
        const { clientId } = action.meta.arg;
        state.foldersList =
          state.foldersList?.map(client =>
            client.clientId === clientId ? { ...client, clientMemberList: action.payload } : client,
          ) || null;
      })
      .addCase(getLatestDatabaseItems.fulfilled, (state, action) => {
        state.latestDatabaseItems = action.payload;
      })
      .addCase(createLinkItem.fulfilled, (_, action) => {
        toast.success(i18n.t('resources.modals.toast.link.new', { name: action.meta.arg.label }));
      })
      .addCase(updateLinkItem.fulfilled, (_, action) => {
        toast.success(i18n.t('resources.modals.toast.link.edit', { name: action.meta.arg.label }));
      })
      .addCase(createFolder.fulfilled, (_, action) => {
        toast.success(i18n.t('resources.modals.toast.folder.new', { name: action.meta.arg.name }));
      })
      .addCase(updateFolder.fulfilled, (_, action) => {
        toast.success(i18n.t('resources.modals.toast.folder.edit', { name: action.meta.arg.name }));
      })
      .addCase(deleteFolder.fulfilled, (_, action) => {
        toast.success(i18n.t('resources.modals.toast.folder.delete', { name: action.meta.arg.name }));
      })
      .addCase(updateClientBranch.fulfilled, (state, action) => {
        if (state.foldersList === null) return;
        const { type, clientId } = action.payload;
        const folders = isOfType<IUpdatedDatabaseFolderTreeBranch>(action.payload, 'databases')
          ? action.payload.databases
          : action.payload.folders;
        state.foldersList = state.foldersList?.map(clientFolders =>
          clientFolders.clientId === clientId ? { ...clientFolders, [type]: folders } : clientFolders,
        );
      })
      .addMatcher(action => isFulfilledAction(action) && isActionFromSlice(action, SLICES.RESOURCES), onFulfilledAction)
      .addMatcher(action => isRejectedAction(action) && isActionFromSlice(action, SLICES.RESOURCES), onRejectedAction)
      .addMatcher(action => isPendingAction(action) && isActionFromSlice(action, SLICES.RESOURCES), onPendingAction);
  },
});

// actions
export const {
  clearState,
  previewDatabaseViewItem,
  setDatabaseViewQueryParams,
  closePreview,
  clearAttachments,
  clearSelectedResourceDetials,
} = slice.actions;

export default slice.reducer;
